import { merchantApi } from '@/config/axios'

/** A 端门户：走 /app-api，带 Agent-Token */
const portalBase = '/app-api/agent/portal'
const TOKEN_KEY = 'agent_portal_token'

export const getAgentPortalToken = () => localStorage.getItem(TOKEN_KEY) || ''
export const setAgentPortalToken = (token: string) => localStorage.setItem(TOKEN_KEY, token)
export const clearAgentPortalToken = () => {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem('agent_portal_profile')
  localStorage.removeItem('agent_portal_name')
  localStorage.removeItem('agent_portal_id')
  localStorage.removeItem('agent_portal_no')
  localStorage.removeItem('agent_portal_username')
}

/** 只用 Agent-Token；isToken:false 禁止 axios 挂商家 Authorization */
const headers = () => ({
  'Agent-Token': getAgentPortalToken(),
  isToken: false
})

/** 公开入驻页在 PC 用户端（yudao-ui-pc），不在管理端 */
export const getAgentApplyPublicBase = () => {
  const base = (import.meta.env.VITE_PC_WEB_URL as string) || ''
  return (base || 'http://localhost:3100').replace(/\/$/, '')
}

/** 二级代理邀请（公开入驻）/ 商家拓客（商家中心归属确认） */
export const buildAgentApplyInviteUrl = (query: Record<string, string | number | undefined | null>) => {
  const params = new URLSearchParams()
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && `${v}` !== '') params.set(k, String(v))
  })
  const qs = params.toString()
  return `${getAgentApplyPublicBase()}/agent-apply${qs ? `?${qs}` : ''}`
}

/**
 * 名下商家「拓客」：跳商家中心归属确认页，带 inviteCode=代理编号。
 * 勿再用 agent-apply?invite=（那是一级代理入驻）。
 */
export const buildMerchantBelongingInviteUrl = (inviteCode: string) => {
  const code = (inviteCode || '').trim()
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const base = (import.meta.env.VITE_BASE_PATH as string) || '/'
  const prefix = base.endsWith('/') ? base.slice(0, -1) : base
  const path = `${prefix}/store/belonging/confirm`
  const qs = code ? `?inviteCode=${encodeURIComponent(code)}` : ''
  return `${origin}${path}${qs}`
}

export interface AgentPortalLoginVO {
  token: string
  agentId: number
  agentNo?: string
  name?: string
  level?: number
  regionName?: string
  status?: number
}

export interface AgentPortalSoftwareProfit {
  softwareId?: string
  softwareName?: string
  amount?: number
}

export interface AgentPortalWorkbenchVO {
  agentId?: number
  name?: string
  agentNo?: string
  level?: number
  status?: number
  regionName?: string
  regionExclusive?: boolean
  currentPeriod?: string
  merchantCount?: number
  totalProfit?: number
  monthProfit?: number
  unsettledProfit?: number
  waitConfirmAmount?: number
  waitConfirmCount?: number
  subCount?: number
  subProfit?: number
  softwareProfits?: AgentPortalSoftwareProfit[]
}

export const portalLogin = (data: { username: string; password: string }) =>
  merchantApi.post<AgentPortalLoginVO>({
    url: `${portalBase}/login`,
    data,
    // 登录前无 Agent-Token；显式标记免挂商家 Bearer，避免 401「账号未登录」被误判
    headers: { isToken: false }
  })

export const portalLogout = () =>
  merchantApi.post({ url: `${portalBase}/logout`, headers: headers() })

export const getPortalWorkbench = () =>
  merchantApi.get<AgentPortalWorkbenchVO>({ url: `${portalBase}/workbench`, headers: headers() })

export const getPortalMerchants = () =>
  merchantApi.get({ url: `${portalBase}/merchants`, headers: headers() })

export const getPortalLedgers = () =>
  merchantApi.get({ url: `${portalBase}/ledgers`, headers: headers() })

export const exportPortalLedgers = () =>
  merchantApi.download({
    url: `${portalBase}/ledgers/export`,
    headers: headers()
  })

export const getPortalRegionMap = () =>
  merchantApi.get({ url: `${portalBase}/region-map`, headers: headers() })

export const sendPortalBankSms = () =>
  merchantApi.post({ url: `${portalBase}/account/send-sms`, headers: headers() })

export const getPortalSettles = () =>
  merchantApi.get({ url: `${portalBase}/settles`, headers: headers() })

export const confirmPortalSettle = (id: number) =>
  merchantApi.put({ url: `${portalBase}/settle/confirm`, params: { id }, headers: headers() })

export const disputePortalSettle = (data: { settlementId: number; reason: string }) =>
  merchantApi.put({ url: `${portalBase}/settle/dispute`, data, headers: headers() })

export const getPortalAccount = () =>
  merchantApi.get({ url: `${portalBase}/account`, headers: headers() })

export const updatePortalAccount = (data: any) =>
  merchantApi.put({ url: `${portalBase}/account/update`, data, headers: headers() })

export const getPortalSubs = () =>
  merchantApi.get({ url: `${portalBase}/subs`, headers: headers() })

export const updatePortalSubRate = (data: { subId: number; subRate: number }) =>
  merchantApi.put({ url: `${portalBase}/subs/rate`, data, headers: headers() })
